import useLocalStorage from 'src/utils/useLocalStorage';

export const BATCH_HANDLE_STORAGE_KEY = 'bulk-promotion-batch-handle';

/**
 * Bumping this discards every stored handle. The underlying hook has no migration hook, so a
 * handle whose shape changed is rejected on read rather than repaired.
 */
export const BATCH_HANDLE_VERSION = 2;

export interface BatchHandle {
    version: number;
    batchId: string;
    componentType: string;
    submittedAt: string;

    /*
     * The period the batch was submitted into. Stored because reading a batch back requires one —
     * accountingPeriod is @required @httpQuery on GetBulkPromotionRequest — so holding it here makes
     * recovery independent of the open period still being resolvable when the operator returns.
     */
    accountingPeriod: string;
}

export function isValidBatchHandle(candidate: any): candidate is BatchHandle {
    return (
        !!candidate &&
        candidate.version === BATCH_HANDLE_VERSION &&
        typeof candidate.batchId === 'string' &&
        candidate.batchId.length > 0 &&
        typeof candidate.componentType === 'string' &&
        typeof candidate.submittedAt === 'string' &&
        // Present but allowed to be empty: requiring a value would discard the handle in exactly the
        // case recovery exists for, and losing the id is worse than finding the period later.
        typeof candidate.accountingPeriod === 'string'
    );
}

export function createBatchHandle(batchId: string, componentType: string, submittedAt: string,
                                  accountingPeriod: string): BatchHandle {
    return { version: BATCH_HANDLE_VERSION, batchId, componentType, submittedAt, accountingPeriod };
}

/**
 * The batch this browser last submitted, so an operator who closed the tab can recover the outcome without
 * re-pasting. Only the id and period are stored — no component data, no figures, no identity.
 *
 * Two limits come from the storage hook: there is no expiry, and writes are not observed by other tabs, so
 * two tabs can hold different handles until each reloads.
 */
export default function useBatchHandle(): [BatchHandle | null, (handle: BatchHandle | null) => void] {
    const [stored, setStored] = useLocalStorage.getStorageKeyFromLocalStorage(BATCH_HANDLE_STORAGE_KEY, null);
    const handle = isValidBatchHandle(stored) ? stored : null;
    return [handle, setStored];
}
